import React from 'react';
import { 
  ChevronRight, 
  ShieldCheck, 
  Zap, 
  BarChart3, 
  ArrowRight, 
  CheckCircle2, 
  Headphones, 
  Activity,
  HeartPulse, 
  XCircle,
  TrendingUp,
  Lock,       // Added for Trust Section
  Award,      // Added for Trust Section
  FileCheck   // Added for Trust Section
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import FadeIn from '../components/FadeIn';

// Asset Imports
import heroImage from '../assets/images/642922.png';
import processDiagram from '../assets/images/revenue-cycle-management-solutio.png';

const Home = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      
      {/* 1. PREMIUM HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center px-6 md:px-12 bg-[#1e3a8a] overflow-hidden">
        <motion.div 
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1.05, opacity: 0.2 }}
          transition={{ duration: 2 }}
          className="absolute inset-0"
        >
          <img src={heroImage} alt="Healthcare Tech" className="w-full h-full object-cover" />
        </motion.div>
        
        <div className="relative z-10 max-w-6xl mx-auto">
          <FadeIn direction="down">
            <div className="inline-flex items-center gap-2 bg-[#14b8a6]/10 border border-[#14b8a6]/20 px-4 py-2 rounded-full mb-8">
              <span className="w-2 h-2 bg-[#14b8a6] rounded-full animate-pulse"></span>
              <span className="text-[#14b8a6] text-xs font-bold uppercase tracking-[0.2em]">Next-Gen Practice Management</span>
            </div>
          </FadeIn>
          
          <FadeIn delay={0.2}>
            <h1 className="text-5xl md:text-8xl font-black text-white leading-[1.1] mb-8">
              Maximize Your <span className="text-[#14b8a6]">Revenue.</span><br />
              Modernize Your Practice.
            </h1>
          </FadeIn>
          
          <FadeIn delay={0.4}>
            <p className="text-xl md:text-2xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
              We bridge the gap between Medical Billing and Specialized IT Solutions. 
              One partner for your total practice health.
            </p>
          </FadeIn>
          
          <FadeIn delay={0.6}>
            <div className="flex flex-wrap gap-5">
              <Link to="/contact" className="bg-[#14b8a6] text-white px-10 py-5 rounded-2xl font-bold text-lg flex items-center gap-3 hover:bg-[#0d9488] transition-all shadow-2xl hover:-translate-y-2 active:scale-95">
                Request Free Assessment <ChevronRight size={20} />
              </Link>
              <Link to="/services" className="bg-white/5 border border-white/20 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all backdrop-blur-sm active:scale-95">
                Explore Our Services
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 2. STATS & SOCIAL PROOF */}
      <div className="relative z-20 -mt-16 max-w-7xl mx-auto px-6">
        <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-12 border border-slate-100">
          {[
            { val: "98%", label: "Clean Claim Rate", icon: <CheckCircle2 className="text-[#14b8a6]" /> },
            { val: "70%", label: "Reduced IT Costs", icon: <Zap className="text-[#14b8a6]" /> },
            { val: "500+", label: "Satisfied Providers", icon: <Users className="text-[#14b8a6]" /> }
          ].map((stat, i) => (
            <FadeIn key={i} delay={i * 0.2}>
              <div className="flex flex-col items-center text-center group cursor-default">
                <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#14b8a6]/10 group-hover:scale-110 transition-all duration-300">{stat.icon}</div>
                <p className="text-5xl font-black text-[#1e3a8a] mb-2">{stat.val}</p>
                <p className="text-sm text-slate-500 uppercase tracking-widest font-bold">{stat.label}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

      {/* 3. CORE DIFFERENTIATOR */}
      <section className="py-32 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <FadeIn direction="right">
            <div>
              <h2 className="text-[#14b8a6] font-bold tracking-widest uppercase text-sm mb-4">Why OptiRev</h2>
              <h3 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-8 leading-tight">
                Unified Financial and Technological Health.
              </h3>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                Most billing companies struggle with fragmented systems. We solve the challenges of 
                interoperability, ensuring data flows securely from patient care to final payment.
              </p>
              <div className="space-y-4">
                {[
                  "Proactive Denial Management",
                  "HIPAA-Managed IT Infrastructure",
                  "Seamless EHR/EMR Integration",
                  "24/7 Dedicated Support"
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="bg-[#14b8a6] rounded-full p-1"><CheckCircle2 size={16} className="text-white" /></div>
                    <span className="font-bold text-[#1e3a8a]">{point}</span>
                  </div>
                ))}
              </div>
              <Link to="/why-optirev" className="inline-flex items-center gap-2 mt-12 text-[#14b8a6] font-bold hover:gap-4 transition-all">
                Learn More About Our Difference <ArrowRight size={20} />
              </Link>
            </div>
          </FadeIn>
          
          <FadeIn direction="left" delay={0.3}>
            <div className="bg-[#1e3a8a] p-12 rounded-[3rem] text-white shadow-xl hover:-translate-y-2 transition-all duration-500">
              <h4 className="text-2xl font-bold mb-6 italic">"OptiRev improved our revenue by 40% in just 3 months."</h4>
              <p className="text-[#14b8a6] font-bold">— Practice Administrator</p>
              <div className="mt-10 pt-10 border-t border-white/10 grid grid-cols-2 gap-8">
                <div>
                  <p className="text-3xl font-black text-[#14b8a6]">+30%</p>
                  <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">Revenue Growth</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-[#14b8a6]">+60%</p>
                  <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">Claim Approvals</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. SPECIALTY EXPERTISE */}
      <section className="py-24 px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <FadeIn direction="down">
            <div className="text-center mb-16">
              <h2 className="text-[#14b8a6] font-bold tracking-widest uppercase text-sm mb-4">Domain Expertise</h2>
              <h3 className="text-4xl font-bold text-[#1e3a8a]">Expertise Across Specialties</h3>
            </div>
          </FadeIn>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { t: "General Practice", i: <Activity size={32} /> },
              { t: "Mental Health", i: <Headphones size={32} /> },
              { t: "Cardiology", i: <HeartPulse size={32} /> },
              { t: "Orthopedics", i: <ShieldCheck size={32} /> }
            ].map((spec, idx) => (
              <FadeIn key={idx} delay={idx * 0.1}>
                <div className="p-8 rounded-3xl border border-slate-200 bg-white text-center hover:bg-[#1e3a8a] hover:text-white transition-all duration-500 group cursor-default">
                  <div className="text-[#14b8a6] mb-4 flex justify-center group-hover:scale-110 transition-transform">{spec.i}</div>
                  <h4 className="font-bold text-sm uppercase tracking-wide">{spec.t}</h4>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* 5. TECH ADVANTAGE */}
      <section className="py-32 px-8 bg-[#1e3a8a] relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <FadeIn direction="right">
            <h2 className="text-[#14b8a6] font-bold tracking-widest uppercase text-sm mb-4">Next-Gen Infrastructure</h2>
            <h3 className="text-4xl font-bold text-white mb-8">Robust Healthcare Tech Stack</h3>
            <div className="space-y-6">
              {[
                { t: "AI-Driven Analytics", d: "Identifying revenue leaks before they happen." },
                { t: "Cloud-Native Security", d: "Military-grade encryption for all PHI data." },
                { t: "Seamless API Integration", d: "Connecting your EMR/EHR to our RCM engine." }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#14b8a6] flex items-center justify-center shrink-0"><Zap size={20} className="text-white" /></div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{item.t}</h4>
                    <p className="text-slate-400 text-sm">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn direction="left" delay={0.3}>
            <div className="relative group p-4">
              <img src={heroImage} alt="IT Infrastructure" className="rounded-[2rem] border border-white/10 shadow-2xl transition-transform duration-700 group-hover:scale-105" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. NEW: TRUST & COMPLIANCE SECTION */}
      <section className="py-24 px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <FadeIn direction="up">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-[#1e3a8a]/5 border border-[#1e3a8a]/10 px-4 py-2 rounded-full mb-6">
                 <ShieldCheck size={16} className="text-[#1e3a8a]" />
                 <span className="text-[#1e3a8a] text-xs font-bold uppercase tracking-widest">Bank-Grade Security</span>
              </div>
              <h2 className="text-4xl font-bold text-[#1e3a8a] mb-6">Uncompromised Compliance</h2>
              <p className="text-slate-500 max-w-2xl mx-auto">
                Your data security is non-negotiable. We adhere to the strictest US healthcare standards 
                to protect your practice and your patients.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                title: "HIPAA Compliant", 
                desc: "Full adherence to Privacy & Security Rules with end-to-end AES-256 encryption.", 
                icon: <Lock className="text-white" size={24} /> 
              },
              { 
                title: "AAPC Certified", 
                desc: "Our billing specialists hold active CPC and CPB certifications.", 
                icon: <Award className="text-white" size={24} /> 
              },
              { 
                title: "SOC-2 Type II", 
                desc: "Regular third-party audits ensuring maximum data integrity and availability.", 
                icon: <ShieldCheck className="text-white" size={24} /> 
              },
              { 
                title: "Clean Coding", 
                desc: "Rigorous multi-tier reviews to prevent fraud and ensure audit readiness.", 
                icon: <FileCheck className="text-white" size={24} /> 
              }
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group h-full">
                  <div className="w-14 h-14 bg-[#14b8a6] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#14b8a6]/20 group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <h4 className="text-xl font-bold text-[#1e3a8a] mb-3">{item.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* 7. COST OF INACTION */}
      <section className="py-24 px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeIn direction="up">
            <div className="text-center mb-16">
              <h3 className="text-4xl font-bold text-[#1e3a8a]">Stop Leaving Revenue on the Table</h3>
              <p className="text-slate-500 mt-4">Traditional billing vs. OptiRev Integrated RCM.</p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 rounded-[3rem] overflow-hidden border border-slate-100 shadow-2xl">
            <div className="p-12 bg-slate-50">
              <h4 className="text-xl font-bold text-slate-400 mb-8 uppercase tracking-widest text-sm">Traditional Model</h4>
              <ul className="space-y-6">
                {["75% Clean Claim Rate", "Fragmented IT Systems", "Slow Denial Response", "High Admin Overhead"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-400 line-through"><XCircle size={18} /> {item}</li>
                ))}
              </ul>
            </div>
            <div className="p-12 bg-[#1e3a8a] text-white">
              <h4 className="text-xl font-bold text-[#14b8a6] mb-8 uppercase tracking-widest text-sm">OptiRev Model</h4>
              <ul className="space-y-6">
                {["98% Clean Claim Rate", "Unified Cloud Workflows", "Instant Denial Mgmt", "Reduced Overhead Costs"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 font-bold"><CheckCircle2 size={18} className="text-[#14b8a6]" /> {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. PROCESS VISUALIZATION */}
      <section className="py-32 bg-slate-50 border-y">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn direction="right">
            <motion.img 
              src={processDiagram} alt="RCM Process" className="w-full max-w-lg mx-auto drop-shadow-3xl"
              animate={{ rotate: 360 }} transition={{ duration: 100, repeat: Infinity, ease: "linear" }}
            />
          </FadeIn>
          <div className="space-y-12">
            {[
              { n: "01", t: "Discover", d: "Audit of current RCM and IT bottlenecks." },
              { n: "02", t: "Analyze", d: "Gap discovery and revenue leak identification." },
              { n: "03", t: "Optimize", d: "Deploying integrated billing and IT workflows." },
              { n: "04", t: "Scale", d: "Ongoing support for long-term growth." }
            ].map((step, i) => (
              <FadeIn key={i} delay={i * 0.2} direction="left">
                <div className="flex gap-8 group cursor-default">
                  <span className="text-5xl font-black text-slate-200 group-hover:text-[#14b8a6] transition-colors">{step.n}</span>
                  <div>
                    <h4 className="text-2xl font-bold text-[#1e3a8a] group-hover:text-[#14b8a6] transition-colors mb-2">{step.t}</h4>
                    <p className="text-slate-500">{step.d}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section className="py-24 px-6">
        <FadeIn direction="up">
          <div className="max-w-6xl mx-auto bg-[#1e3a8a] rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden group">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black text-white mb-8">Ready to Modernize?</h2>
              <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto">One partner for your total practice health.</p>
              <Link to="/contact" className="bg-[#14b8a6] text-white px-12 py-6 rounded-2xl font-bold text-xl hover:shadow-2xl transition-all inline-block hover:-translate-y-2 active:scale-95">
                Get Started with OptiRev
              </Link>
            </div>
            <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }} transition={{ duration: 5, repeat: Infinity }} className="absolute top-0 right-0 w-64 h-64 bg-[#14b8a6] rounded-full blur-[100px]" />
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

const Users = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
);

export default Home;